import { useState, useContext, useCallback } from 'react';
import { Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { es, enUS, ca, pt, de, fr, Locale } from 'date-fns/locale';
import {
    AppContext,
    SttTopControls,
    SttTable,
    SttChipFileFormat,
    SttChipFileType,
    SttFileDialog,
    SttButtonFab,
    UploadFileDialog,
    SttPage,
} from '../../sporttia/all';
import { updateElementInArray } from '../../lib/utils';

type FileType = {
    id: number;
    name?: string;
    created?: string;
    type?: string;
    format?: string;
    public?: boolean;
};

type FilesResponse = {
    rows: FileType[];
    count: number;
    page: number;
};

type FilterType = {
    name: string;
    type: string | null;
    format: string | null;
};

type FileTypeOption = {
    label?: string;
    value?: string;
};

const localeMap: Record<string, Locale> = {
    es,
    en: enUS,
    ca,
    pt,
    de,
    fr,
    mx: es,
};

export default function Files(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t, i18n } = useTranslation();

    const [files, setFiles] = useState<FilesResponse>();
    const [openFileId, setOpenFileId] = useState<number>();
    const [uploadFileOpen, setUploadFileOpen] = useState(false);
    const [filters, setFilters] = useState<FilterType>({
        name: '',
        type: null,
        format: null,
    });

    const formatDate = useCallback(
        (date?: string): string => {
            if (!date) return '';
            const d = new Date(date);
            const locale = localeMap[i18n.language] || es;
            const isCurrentYear = d.getFullYear() === new Date().getFullYear();
            return format(d, isCurrentYear ? 'd MMM' : 'd MMM yyyy', {
                locale,
            });
        },
        [i18n.language],
    );

    const loadFiles = useCallback(
        (p = { page: 1, rows: 20 }) => {
            cxt?.api('GET', `/scs/${cxt?.sc?.id}/files`, {
                params: {
                    ...filters,
                    page: p.page,
                    rows: p.rows,
                },
                success: (r: FilesResponse) => {
                    setFiles(r);
                },
            });
        },
        [cxt, filters],
    );

    const openFile = useCallback((file: FileType) => {
        setOpenFileId(file.id);
    }, []);

    const updateFile = useCallback(
        (file: { id: number; [key: string]: unknown }) => {
            if (!files) return;
            setFiles({
                ...files,
                rows: updateElementInArray(files.rows, file as FileType),
            });
            setOpenFileId(undefined);
        },
        [files],
    );

    const getTranslatedFileTypes = useCallback((): FileTypeOption[] => {
        if (!cxt?.constants?.fileTypes) return [];
        return cxt.constants.fileTypes.map((ft: FileTypeOption) => {
            const match = ft.label?.match(/^\[\[ft\.(.+)\]\]$/);
            return {
                ...ft,
                label:
                    match && match[1]
                        ? t(`FT.${match[1].toUpperCase()}`)
                        : ft.label,
            };
        });
    }, [cxt?.constants?.fileTypes, t]);

    const handleFilterChange = useCallback(
        ({ name, value }: { name: string | undefined; value: unknown }) => {
            if (!name) return;
            setFilters((prev) => ({ ...prev, [name]: value }));
        },
        [],
    );

    return (
        <SttPage title={t('Documents')}>
            <SttTopControls
                p={2}
                fields={
                    [
                        {
                            caption: t('Name'),
                            name: 'name',
                            type: 'text',
                            value: filters.name,
                            onEnterKey: loadFiles,
                            xs: 12,
                            sm: 6,
                            md: 4,
                            lg: 3,
                        },
                    ] as React.ComponentProps<typeof SttTopControls>['fields']
                }
                extraFields={
                    [
                        {
                            caption: t('Type'),
                            name: 'type',
                            type: 'select',
                            value: filters.type ?? '',
                            options: getTranslatedFileTypes(),
                            cleanable: true,
                        },
                        {
                            caption: t('Format'),
                            name: 'format',
                            type: 'select',
                            value: filters.format ?? '',
                            options: cxt?.constants?.fileFormats || [],
                            cleanable: true,
                        },
                    ] as React.ComponentProps<
                        typeof SttTopControls
                    >['extraFields']
                }
                onChange={handleFilterChange}
                onFilter={loadFiles}
            />

            <SttTable
                data={files}
                columns={[
                    {
                        title: t('Created'),
                        field: 'created',
                        value: (row: FileType) => formatDate(row.created),
                    },
                    {
                        align: 'left',
                        title: t('Name'),
                        value: (row: FileType) => (
                            <Link
                                underline="hover"
                                color="primary"
                                sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                                onClick={() => openFile(row)}
                            >
                                {row.name || t('Noname')}
                            </Link>
                        ),
                    },
                    {
                        title: t('Type'),
                        field: 'type',
                        value: (row: FileType) =>
                            row.type ? (
                                <SttChipFileType type={row.type} />
                            ) : null,
                    },
                    {
                        title: t('Format'),
                        value: (row: FileType) =>
                            row.format ? (
                                <SttChipFileFormat format={row.format} />
                            ) : null,
                    },
                    {
                        type: 'binaryStatus',
                        title: t('Public'),
                        field: 'public',
                    },
                ]}
                onFetch={loadFiles}
            />

            <SttButtonFab onClick={() => setUploadFileOpen(true)} />

            <UploadFileDialog
                open={uploadFileOpen}
                types={[
                    'SC',
                    'SC_LOGO',
                    'FACILITY',
                    'USER_IMPORT',
                    'Q60_MOD_2',
                ]}
                onClose={() => setUploadFileOpen(false)}
                onUploaded={() => {
                    setUploadFileOpen(false);
                    loadFiles();
                }}
            />

            <SttFileDialog
                idFile={openFileId}
                onClose={() => setOpenFileId(undefined)}
                onUpdate={updateFile}
            />
        </SttPage>
    );
}
